import { useRef, useEffect } from 'react';
import { FlexCol } from '@components/layout/Flex';
import {
    DataTableWrapper,
    StyledTable,
    DataTh,
    DataTd,
    RowNumberTh,
    RowNumberTd,
    ShowMoreButton,
} from './DataTable.style';

export interface DataTableProps {
    columns: string[];
    rows: string[][];
    totalRows?: number;
    hasMore?: boolean;
    onLoadMore?: () => void;
    isLoadingMore?: boolean;
}

export function DataTable({ columns, rows, totalRows, hasMore, onLoadMore, isLoadingMore }: DataTableProps) {
    const wrapperRef = useRef<HTMLDivElement>(null);
    const prevRowCount = useRef(rows.length);

    // Scroll to bottom when new rows are appended
    useEffect(() => {
        if (rows.length > prevRowCount.current && wrapperRef.current) {
            wrapperRef.current.scrollTop = wrapperRef.current.scrollHeight;
        }
        prevRowCount.current = rows.length;
    }, [rows.length]);

    if (columns.length === 0) {
        return null;
    }

    const showButton = hasMore && onLoadMore;
    const total = totalRows ?? rows.length;
    const remainingLabel = total > rows.length ? ` (${(total - rows.length).toLocaleString()} remaining)` : '';

    return (
        <FlexCol $gap={8}>
            <DataTableWrapper ref={wrapperRef}>
                <StyledTable>
                    <thead>
                        <tr>
                            <RowNumberTh>#</RowNumberTh>
                            {columns.map((col, i) => (
                                <DataTh key={i}>{col}</DataTh>
                            ))}
                        </tr>
                    </thead>
                    <tbody>
                        {rows.map((row, ri) => (
                            <tr key={ri}>
                                <RowNumberTd>{ri + 1}</RowNumberTd>
                                {columns.map((_, ci) => (
                                    <DataTd key={ci}>{row[ci] ?? ''}</DataTd>
                                ))}
                            </tr>
                        ))}
                    </tbody>
                </StyledTable>
            </DataTableWrapper>
            {showButton && (
                <ShowMoreButton onClick={onLoadMore} disabled={isLoadingMore}>
                    {isLoadingMore ? 'Loading...' : `Load more${remainingLabel}`}
                </ShowMoreButton>
            )}
        </FlexCol>
    );
}
